<template>
  <vee-form v-slot="{ validate }" as="">
    <form
      ref="form"
      class="standard-form"
      action="/my/account/subscription/add-wallet-discount"
      method="post"
      novalidate
      @submit.prevent="openModal(validate)"
    >
      <div class="container -flush">
        <checkbox-input v-model="formData.useWallet" input-id="useWallet">
          <template #label>
            <span>
              Use FB Wallet Funds
              <span class="wallet-balance">
                - Current Balance: {{ moneyFormat(walletBalance.toString()) }}
              </span>
            </span>
          </template>
        </checkbox-input>

        <text-input
          v-show="formData.useWallet"
          v-model="formData.walletAmount"
          input-id="walletAmount"
          type="number"
          label="Amount to use from your wallet:"
          placeholder="0.00"
          :rules="walletAmountRules"
          validate-as="Wallet amount"
          :max="maxWalletAmount"
          min="0"
          @input="restrictWalletAmount"
        />

        <div v-show="formData.useWallet" class="form-actions">
          <div class="form-action">
            <csrf-input />
            <submit-button
              type="submit"
              class="btn -main"
              :submitting="processing"
              label="Apply Wallet Funds"
              :submitting-label="submittingLabel"
              @submit-action="openModal(validate)"
            />
          </div>
        </div>
      </div>
    </form>
  </vee-form>
</template>

<script>
import { defineComponent } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useModalsStore } from '@/stores/modals'
import { compareFloats, moneyFormat } from '@/utils/helpers'
import { initExtraValidationRules } from '@/utils/forms/validation-rules'
import { Form as VeeForm } from 'vee-validate'
import TextInput from '@/components/forms/text-input.vue'
import CheckboxInput from '@/components/forms/checkbox-input.vue'
import SubmitButton from '../buttons/submit-button.vue'
import CsrfInput from '../forms/csrf-input.vue'

export default defineComponent({
  name: 'SubscriptionAddWalletDiscount',
  components: {
    VeeForm,
    CheckboxInput,
    TextInput,
    SubmitButton,
    CsrfInput,
  },
  props: {
    // form input, so string
    walletBalance: {
      type: String,
      default: '0',
    },
    max: {
      type: String,
      default: '',
    },
  },
  data() {
    // determine default wallet amount
    let walletAmount = this.walletBalance

    if (this.max && compareFloats(this.walletBalance, '>=', this.max)) {
      walletAmount = this.max
    }

    return {
      formData: {
        useWallet: this.walletBalance > 0,
        walletAmount: walletAmount,
      },
      processing: false,
    }
  },

  /* computed */
  computed: {
    ...mapState(useModalsStore, ['simpleModal']),

    submittingLabel() {
      return 'Processing...'
    },

    // check to see if string balance is truthy
    hasWalletBalance() {
      return !!Number.parseFloat(this.walletBalance)
    },

    // validation rules if useWallet is checked
    walletAmountRules() {
      if (this.formData.useWallet) {
        return `required|max_value:${this.maxWalletAmount}|min_value:0.01`
      }

      return ''
    },

    /**
     * Users can apply any amount up to wallet balance unless a max prop is specified
     */
    maxWalletAmount() {
      return this.max ? this.max : this.walletBalance
    },

    modalId() {
      return 'subscription-add-wallet-discount-modal'
    },
  },

  /* watchers */
  watch: {
    'simpleModal.action'(newAction, prevAction) {
      if (
        this.simpleModal.id === this.modalId &&
        newAction !== prevAction &&
        newAction === 'confirm'
      ) {
        this.handleSubmit()
      }
    },
  },

  /* lifecycle */
  created() {
    initExtraValidationRules()
  },

  /* methods */
  methods: {
    ...mapActions(useModalsStore, ['openSimpleModal']),

    // use for display
    moneyFormat(val) {
      return moneyFormat(val)
    },

    /**
     * do not allow formData.walletAmount to ever be larger than total
     */
    restrictWalletAmount(e) {
      // force string, vue3 converts input type number to string
      const newAmount = `${e.target.value}`
      const maxWalletAmount = this.maxWalletAmount

      // over max amount, so just set to max
      if (compareFloats(newAmount, '>', maxWalletAmount)) {
        this.formData.walletAmount = maxWalletAmount
        return
      }

      // negative
      if (compareFloats(newAmount, '<', '0.00')) {
        this.formData.walletAmount = '0.00'
        return
      }

      // do not allow more than two decimal places
      if (newAmount.indexOf('.') !== -1 && newAmount.split('.')[1].length > 2) {
        this.formData.walletAmount = Number.parseFloat(newAmount).toFixed(2)
      }
    },

    async openModal(validate) {
      // first pass validation
      const results = await validate()

      // invalid form
      if (!results.valid) {
        return
      }

      // only open if using wallet funds
      if (!this.formData.useWallet) {
        return
      }

      this.openSimpleModal({
        type: 'CONFIRM',
        id: this.modalId,
        props: {
          heading: 'Fitness Blender Wallet',
          subheading: `Are you sure you want to transfer ${moneyFormat(
            this.formData.walletAmount,
          )} from your FB Wallet to FB Plus?`,
          buttonText: 'Yes',
          cancelText: 'No',
        },
      })
    },

    /**
     * Traditional form submit
     */
    async handleSubmit() {
      // avoid double submit
      if (this.processing) {
        return
      }

      this.processing = true

      // submit form and make sure watchers settle
      this.$nextTick(() => {
        this.$refs.form.submit()
      })
    },
  },
})
</script>
